/* eslint-disable camelcase */
import React from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import WebpImage from '../webp-image';
import MoneyUtils from '../../services/MoneyUtils';
import Features from '../../services/Features';
import './style.css';
import Environment from '../../services/Environment';

const getPlanImage = (months, minimumMonths) => {
  let imageName = 'subSelectionMonthly';
  if (months === '12' || minimumMonths === 12) {
    imageName = 'subSelection12';
  } else if (months === '9' || minimumMonths === 9) {
    imageName = 'subSelection9';
  } else if (months === '6' || minimumMonths === 6) {
    imageName = 'subSelection6';
  } else if (months === '3') {
    imageName = 'subSelection3';
  }
  return imageName;
};

const getHighlightClassName = (highlight) => {
  if (highlight.className) {
    return `subBoxSubHeader left-align ${highlight.className}`;
  }
  return 'subBoxSubHeader left-align';
};

const formatBundledPricing = (price) => {
  const slicedPrice = price.split('.');

  return (
    <>
      <sup>$</sup>
      {slicedPrice[0]}
      <sup>{slicedPrice[1] !== '00' ? slicedPrice[1] : ''}</sup>
    </>
  );
};

const getPinkBoxMessage = (props, selectSubMessage) => {
  if (
    props.referralCode &&
    props.plan.title !== 'Month to Month' &&
    props.plan.months === 'INFINITY'
  ) {
    return `100% off First Box!`;
  }

  if (selectSubMessage) return selectSubMessage;
  if (props.plan.originalCost !== props.plan.costWithCoupon) {
    if (props.plan.couponToAutoPopulate) {
      if (
        props.plan.couponToAutoPopulate.discountPercentage &&
        props.plan.couponToAutoPopulate.discountPercentage > 0
      ) {
        return `${props.plan.couponToAutoPopulate.discountPercentage}% Off First Box!`;
      }
    }
    return `$${props.plan.costWithCoupon} First Box!`;
  }
  return <span>&nbsp;</span>;
};

const renderSubscriptionTag = (plan) => {
  if (plan.tagType === 'BEST_VALUE') {
    return <div className='subscriptionTag'>Best Value</div>;
  }
  if (plan.tagType === 'MOST_POPULAR') {
    return <div className='subscriptionTag'>Most Popular</div>;
  }
  return null;
};

const renderPlanBulletPoints = (props) => {
  if (!props.plan.highlights || !props.plan.highlights.length) {
    return null;
  }
  // eslint-disable-next-line consistent-return, array-callback-return
  const bulletPoints = props.plan.highlights.filter((elem) => {
    // Different points get displayed depending on if this is a referral purchase or not
    if (elem.showForReferralsOrNotOrBoth === 'both') {
      return elem;
    }
    if (elem.showForReferralsOrNotOrBoth === 'yes' && props.referralCode) {
      return elem;
    }
    if (elem.showForReferralsOrNotOrBoth === 'no' && !props.referralCode) {
      return elem;
    }
  });
  return (
    <ul className='bulletpointContainer'>
      {[...bulletPoints]
        .sort((a, b) => a.order - b.order)
        .map((item) => {
          return (
            <li
              key={props.plan._id + item.text}
              className={getHighlightClassName(item)}
            >
              {item.text}
            </li>
          );
        })}
    </ul>
  );
};

const getPlanAsClassName = (plan) => {
  let planClassName = `${plan.title}${plan.months}`;
  planClassName = planClassName.replace(/\s+/g, '');

  return planClassName;
};

const SelectSubscription = (props) => {
  if (!props.plan) {
    return null;
  }

  const history = useHistory();

  /* eslint camelcase: ["warn", {properties: "never"}] */
  const experiment2025_showClickablePdp =
    (Features.getExperiment('2025_consumer_-_clickablepdp').get(
      'showClickablePdp',
      false,
    ) === true) && Environment.store === 'bump';

  let subscriptionCouponFromURLThatMatchesThisPlan;
  let planInfoFromCoupon;
  if (props.subscriptionCouponsFromURL) {
    subscriptionCouponFromURLThatMatchesThisPlan = props.subscriptionCouponsFromURL.find(
      (coupon) => {
        return coupon.exclusivePlans.find(
          (exclusivePlan) => exclusivePlan._id === props.plan._id,
        );
      },
    );
    if (subscriptionCouponFromURLThatMatchesThisPlan) {
      planInfoFromCoupon =
        subscriptionCouponFromURLThatMatchesThisPlan.planInfoOverride;
    }
  }

  const planClassName = getPlanAsClassName(props.plan);
  let buttonClassName = `subSelectButton ${planClassName}`;
  if (props.activeIndex === props.planIndex) {
    buttonClassName = `${buttonClassName} subSelectButtonActiveInCarousel`;
  }
  const selectSubMessage = planInfoFromCoupon?.selectSubMessage
    ? planInfoFromCoupon.selectSubMessage
    : props.plan.selectSubMessage;

  const getSubContainerClass = (plan) => {
    return `subContainer subContainer${getPlanAsClassName(plan)}`;
  };

  const isInfinity = props.plan.months === 'INFINITY';
  const isMonthToMonth = props.plan.title === 'Month to Month';
  const isInfinityWithoutMonthToMonth = isInfinity && !isMonthToMonth;

  let discountPrice;
  if (props.referralCode && isInfinityWithoutMonthToMonth) {
    discountPrice = 0;
  } else if (planInfoFromCoupon?.costWithCoupon) {
    discountPrice = MoneyUtils.formatAsMoney(planInfoFromCoupon.costWithCoupon);
  } else {
    discountPrice = MoneyUtils.formatAsMoney(props.plan.costWithCoupon);
  }

  const goToNextStep = () => {
    return props.nextStep(
      props.plan,
      subscriptionCouponFromURLThatMatchesThisPlan,
    );
  };

  const exp2025_clickableSub_goToPdpPage = () => {
    if (props.plan.months !== 'INFINITY') {
      switch (props.plan.title) {
        case 'Six Month':
          history.push('/products/bundled-six-month');
          break;
        case 'Nine Month':
          history.push('/products/bundled-nine-month');
          break;
        case 'Twelve Month':
          history.push('/products/bundled-twelve-month-pregnancy-subscription');
          break;
        default:
          history.push('/products/bundled-six-month');
          break;
      }
    } else {
      switch (props.plan.title) {
        case 'Three Month':
          history.push('/products/three-month-pregnancy-subscription-box');
          break;
        case 'Six Month':
          history.push('/products/six-month-pregnancy-subscription-box');
          break;
        case 'Nine Month':
          history.push('/products/nine-month-pregnancy-subscription-box');
          break;
        case 'Twelve Month':
          history.push('/products/twelve-month-pregnancy-subscription-box');
          break;
        case 'Month to Month':
          history.push('/products/month-to-month-bump');
          break;
        default:
          history.push('/products/six-month-pregnancy-subscription-box');
          break;
      }
    }
  };

  // experiment2025_showClickablePdp -----------------------------

  const handleContainerClick = () => {
    if (experiment2025_showClickablePdp) {
      exp2025_clickableSub_goToPdpPage();
    }
  };

  const handleContainerKeyPress = (e) => {
    if (
      experiment2025_showClickablePdp &&
      (e.key === 'Enter' || e.key === ' ')
    ) {
      e.preventDefault();
      goToNextStep();
    }
  };

  return (
    <>
      <Col
        className={getSubContainerClass(props.plan)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(experiment2025_showClickablePdp && {
          onClick: handleContainerClick,
          onKeyPress: handleContainerKeyPress,
          tabIndex: 0,
          role: 'button',
          'aria-label': `Select ${props.plan.title} ${
            isInfinity ? '' : 'Bundled '
          }Subscription Plan`,
          style: { cursor: 'pointer' },
        })}
      >
        {renderSubscriptionTag(props.plan)}
        <Row>
          <Col
            xs={8}
            sm={8}
            md={8}
            lg={8}
            style={{ margin: '24px auto 0 auto' }}
          >
            <WebpImage
              imageName={getPlanImage(
                props.plan.months,
                props.plan.minimumMonths,
              )}
              width='300'
              height='180'
              className='subSelectionImage'
              fluid
              alt={`${props.plan.title} Subscription Plan`}
            />
          </Col>
        </Row>
        <Row>
          <Col className='mb-0'>
            <h3 className='subTitle'>{props.plan.title} Subscription</h3>
            {isMonthToMonth && (
              <Row className='no-gutters'>
                <Col>
                  <h4 className='subPlanPrice subFirstBoxPrice'>
                    ${discountPrice}
                  </h4>
                  <h5>Per Month</h5>
                  <div className='subPlanCost mb-2'>
                    <p>&nbsp;</p>
                  </div>
                </Col>
              </Row>
            )}
            {isInfinityWithoutMonthToMonth && (
              <Row className='no-gutters'>
                <Col>
                  <h4 className='subPlanPrice subFirstBoxPrice'>
                    ${discountPrice}
                  </h4>
                  <h5>First Month</h5>
                  <div className='subPlanCost mb-2'>
                    ${props.plan.originalCost} monthly renewal
                  </div>
                </Col>
              </Row>
            )}
            {!isInfinity && (
              <>
                <div className='subBoxHeader1 center-align'>
                  <sup>$</sup>
                  {props.plan.originalCost}
                </div>
                <div className='subPlanPrice inline-text extremelyBold textHighlight'>
                  {formatBundledPricing(discountPrice)}
                </div>
              </>
            )}
          </Col>
        </Row>

        {!experiment2025_showClickablePdp && (
          <Row className='subBoxSubContainer'>
            <Col xs={12} sm={12} md={12} lg={12}>
              {!experiment2025_showClickablePdp &&
                renderPlanBulletPoints(props)}

              <Row className='mt-2 center-align'>
                <Col
                  xs={6}
                  sm={6}
                  md={6}
                  lg={6}
                  className={`inline-text first-box-discount ${
                    props.plan.originalCost !== props.plan.costWithCoupon ||
                    selectSubMessage
                      ? 'first-box-discount-with-contents'
                      : ''
                  }`}
                >
                  {!experiment2025_showClickablePdp &&
                    getPinkBoxMessage(props, selectSubMessage)}
                </Col>
              </Row>
            </Col>
          </Row>
        )}

        <Row>
          <Col xs={12} sm={12} md={12} lg={12}>
            {experiment2025_showClickablePdp && (
              <Button
                variant='primary'
                className={buttonClassName}
                onClick={(e) => {
                  e.stopPropagation();
                  exp2025_clickableSub_goToPdpPage();
                }}
                aria-label={`Add ${props.plan.title} ${
                  isInfinity ? '' : 'Bundled '
                }Subscription Plan to cart`}
              >
                GET STARTED{' '}
              </Button>
            )}

            {!experiment2025_showClickablePdp && (
              <Button
                variant='primary'
                className={buttonClassName}
                onClick={goToNextStep}
                aria-label={`Add ${props.plan.title} ${
                  isInfinity ? '' : 'Bundled '
                }Subscription Plan to cart`}
              >
                SELECT PLAN{' '}
                <FontAwesomeIcon
                  icon={faArrowRight}
                  size='lg'
                  className='ml-2'
                />
              </Button>
            )}

            {isInfinityWithoutMonthToMonth && (
              <p className='selectSubNote'>Cancel or pause at any time.</p>
            )}
            {!isInfinityWithoutMonthToMonth && (
              <p className='selectSubNoteEmpty' />
            )}
          </Col>
        </Row>
      </Col>
    </>
  );
};

export default SelectSubscription;
